import { useEffect } from "react";
import { ActionButton } from "@keystar/ui/button";
import { Divider } from "@keystar/ui/layout";
import { Tooltip, TooltipTrigger } from "@keystar/ui/tooltip";
import { Text } from "@keystar/ui/typography";
import { gql, useMutation, useQuery } from "@keystone-6/core/admin-ui/apollo";
import {
  DeveloperResourcesMenu,
  getHrefFromList,
  NavContainer,
  NavFooter,
  NavItem,
  NavList
} from "@keystone-6/core/admin-ui/components";
import { useRouter } from "@keystone-6/core/admin-ui/router";
import type { NavigationProps } from "@keystone-6/core/admin-ui/components";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";

import { CollapsibleNavGroup } from "./CollapsibleNavGroup";
import { groupNavLists } from "./nav-groups";

const FETCH_SESSION: TypedDocumentNode<{ authenticatedItem: { label: string } | null }> = gql`
  query KsAuthFetchSession {
    authenticatedItem {
      label: email
    }
  }
`;

// Prefix match, "/"-bounded so /user never claims /user-things.
function isListRouteActive(href: string, pathname: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function CustomNavigation({ lists }: NavigationProps) {
  const { data } = useQuery(FETCH_SESSION);
  const { pathname } = useRouter();

  return (
    <NavContainer>
      <NavList>
        <NavItem href="/">Dashboard</NavItem>
        <Divider />
        {groupNavLists(lists).map((group) => (
          <CollapsibleNavGroup
            key={group.title}
            title={group.title}
            isActive={group.lists.some((list) => isListRouteActive(getHrefFromList(list), pathname))}
          >
            {group.lists.map((list) => (
              <NavItem key={list.key} href={getHrefFromList(list)}>
                {list.label}
              </NavItem>
            ))}
          </CollapsibleNavGroup>
        ))}
        <Divider />
        <NavItem href="/CronJobs">Cron Jobs</NavItem>
      </NavList>

      <NavFooter>
        {data?.authenticatedItem && <SignoutButton authItemLabel={data.authenticatedItem.label} />}
        <DeveloperResourcesMenu />
      </NavFooter>
    </NavContainer>
  );
}

const END_SESSION: TypedDocumentNode<{ endSession: boolean }> = gql`
  mutation KsAuthEndSession {
    endSession
  }
`;

function SignoutButton({ authItemLabel }: { authItemLabel: string }) {
  const router = useRouter();
  const [endSession, { data }] = useMutation(END_SESSION);

  useEffect(() => {
    if (data?.endSession) {
      router.push("/signin");
    }
  }, [data, router]);

  return (
    <TooltipTrigger>
      <ActionButton onPress={() => endSession()}>Sign out</ActionButton>
      <Tooltip>
        <Text>
          Signed in as <strong>{authItemLabel}</strong>
        </Text>
      </Tooltip>
    </TooltipTrigger>
  );
}
